import React, { useState } from "react";
import {
  Table,
  Button,
  DatePicker,
  Input,
  Card,
  Spin,
  Tag,
  Drawer,
  Space,
  Descriptions,
} from "antd";
import { EyeOutlined, SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  useMonthlyAttendanceSheetQuery,
  useEmployeeMonthlyLogsQuery,
} from "../../Services";
import { PageHeader } from "../../components";

export const MonthlyAttendanceSheet = () => {
  const [sheetMonth, setSheetMonth] = useState<dayjs.Dayjs>(dayjs());
  const [sheetSearch, setSheetSearch] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const monthStr = sheetMonth.format("YYYY-MM");

  const {
    data: monthlySheet = [],
    isLoading: sheetLoading,
    refetch: refetchSheet,
  } = useMonthlyAttendanceSheetQuery({
    month: monthStr,
    search: sheetSearch,
  });

  const { data: employeeLogs = [], isLoading: logsLoading } =
    useEmployeeMonthlyLogsQuery({
      employee_id: selectedEmployee?.employee_id || "",
      month: monthStr,
    });

  const handleOpenDetailModal = (employee: any) => {
    setSelectedEmployee(employee);
    setDetailModalOpen(true);
  };

  const sheetColumns = [
    {
      title: "Employee",
      key: "employee",
      render: (_: any, record: any) => (
        <div>
          <div className="font-semibold text-gray-800">
            {record.first_name} {record.last_name}
          </div>
          <div className="text-xs text-gray-400 font-mono">
            {record.nic || record.phone}
          </div>
        </div>
      ),
    },
    {
      title: "Designation",
      dataIndex: "designation",
      key: "designation",
      render: (val: string) => <Tag color="blue">{val || "Staff"}</Tag>,
    },
    {
      title: "Present",
      dataIndex: "present_count",
      key: "present_count",
      render: (val: number) => <Tag color="success">{val} Days</Tag>,
    },
    {
      title: "Absent",
      dataIndex: "absent_count",
      key: "absent_count",
      render: (val: number) => <Tag color="error">{val} Days</Tag>,
    },
    {
      title: "Late",
      dataIndex: "late_count",
      key: "late_count",
      render: (val: number) => <Tag color="warning">{val} Days</Tag>,
    },
    {
      title: "Half Day",
      dataIndex: "half_day_count",
      key: "half_day_count",
      render: (val: number) => <Tag color="processing">{val} Days</Tag>,
    },
    {
      title: "Worked Hours",
      dataIndex: "total_worked_hours",
      key: "total_worked_hours",
      render: (val: any) => (
        <span className="font-semibold text-gray-700">
          {parseFloat(val).toFixed(2)} hrs
        </span>
      ),
    },
    {
      title: "OT Hours",
      dataIndex: "total_ot_hours",
      key: "total_ot_hours",
      render: (val: any) => (
        <span
          className={
            parseFloat(val) > 0 ? "font-bold text-green-600" : "text-gray-400"
          }
        >
          {parseFloat(val) > 0 ? `+${parseFloat(val).toFixed(2)} hrs` : "0.00"}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_: any, record: any) => (
        <Button
          type="primary"
          ghost
          icon={<EyeOutlined />}
          onClick={() => handleOpenDetailModal(record)}
          size="small"
        >
          View Logs
        </Button>
      ),
    },
  ];

  const detailColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 120,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val: string) => {
        let color = "default";
        if (val === "Present") color = "success";
        if (val === "Absent") color = "error";
        if (val === "Late") color = "warning";
        if (val === "Half Day") color = "processing";
        return <Tag color={color}>{val}</Tag>;
      },
    },
    {
      title: "First In",
      dataIndex: "check_in",
      key: "check_in",
      render: (val: string) => (val ? val.substring(0, 5) : "-"),
    },
    {
      title: "Last Out",
      dataIndex: "check_out",
      key: "check_out",
      render: (val: string) => (val ? val.substring(0, 5) : "-"),
    },
    {
      title: "Worked (Hrs)",
      dataIndex: "worked_hours",
      key: "worked_hours",
      render: (val: any) => parseFloat(val).toFixed(2),
    },
    {
      title: "OT (Hrs)",
      dataIndex: "ot_hours",
      key: "ot_hours",
      render: (val: any) =>
        parseFloat(val) > 0 ? (
          <span className="font-semibold text-green-600">
            +{parseFloat(val).toFixed(2)}
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: "Clocking Timeline Logs",
      key: "timeline",
      render: (_: any, record: any) => {
        const raw_logs = record.raw_logs || [];
        if (raw_logs.length === 0) {
          return <span className="text-gray-400 text-xs">No scan logs</span>;
        }
        return (
          <Space wrap size={4}>
            {raw_logs.map((log: any, idx: number) => (
              <Tag
                key={idx}
                color={log.type === "In" ? "blue" : "purple"}
                style={{ fontSize: "11px" }}
              >
                <b>{log.type}</b>: {log.time}
              </Tag>
            ))}
          </Space>
        );
      },
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-gray-50/50">
      <PageHeader
        title="Monthly Attendance Sheet"
        subText="View professional monthly attendance summaries for all employees"
        handleButtons={[
          {
            icon: ReloadOutlined,
            title: "Refresh",
            onClick: () => refetchSheet(),
          },
        ]}
        searchBar={
          <div className="flex gap-3">
            <DatePicker
              picker="month"
              value={sheetMonth}
              onChange={(date) => date && setSheetMonth(date)}
              format="YYYY-MM"
              allowClear={false}
              className="w-40 h-9"
            />
            <Input
              placeholder="Search name or NIC..."
              prefix={<SearchOutlined />}
              value={sheetSearch}
              onChange={(e) => setSheetSearch(e.target.value)}
              allowClear
              className="w-60 h-9"
            />
          </div>
        }
      />

      <Card
        className="border-gray-200 shadow-sm rounded-lg overflow-hidden"
        bodyStyle={{ padding: 0 }}
      >
        <Table
          dataSource={monthlySheet}
          columns={sheetColumns}
          rowKey="employee_id"
          loading={sheetLoading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1000 }}
          locale={{
            emptyText: "No attendance records found for the selected month.",
          }}
        />
      </Card>

      <Drawer
        title={
          <div className="flex flex-col gap-0.5">
            <span style={{ fontSize: "16px", fontWeight: 600 }}>
              Employee Attendance Worksheet
            </span>
            <span
              style={{ fontSize: "12px", color: "#8c8c8c", fontWeight: 400 }}
            >
              Date Range: {monthStr}-01 to {monthStr}-31
            </span>
          </div>
        }
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        width={800}
      >
        {selectedEmployee && (
          <Space direction="vertical" size={16} className="w-full">
            <Card size="small" className="bg-gray-50 border-gray-200">
              <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
                <Descriptions.Item label="Employee">
                  <b>
                    {selectedEmployee.first_name} {selectedEmployee.last_name}
                  </b>
                </Descriptions.Item>
                <Descriptions.Item label="Designation">
                  {selectedEmployee.designation || "Staff"}
                </Descriptions.Item>
                <Descriptions.Item label="NIC Number">
                  {selectedEmployee.nic || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Worked Days">
                  <Tag color="success">
                    {selectedEmployee.present_count} Present
                  </Tag>
                  {selectedEmployee.late_count > 0 && (
                    <Tag color="warning">
                      {selectedEmployee.late_count} Late
                    </Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Total Worked">
                  <b>
                    {parseFloat(selectedEmployee.total_worked_hours).toFixed(2)}{" "}
                    hrs
                  </b>
                </Descriptions.Item>
                <Descriptions.Item label="Total Overtime">
                  <span className="text-green-600 font-bold">
                    +{parseFloat(selectedEmployee.total_ot_hours).toFixed(2)}{" "}
                    hrs
                  </span>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Table
              dataSource={employeeLogs}
              columns={detailColumns}
              rowKey="date"
              size="small"
              loading={logsLoading}
              pagination={false}
              scroll={{ y: 400 }}
              locale={{
                emptyText:
                  "No clockings or attendance summary recorded for this month.",
              }}
            />
          </Space>
        )}
      </Drawer>
    </div>
  );
};
